import type {
    EditorConditionNode,
    EditorGroupNode,
    EditorNode,
    ScreenerFeatureDefinition,
    ScreenerGroupOperator
} from '../types/screenerEditor';
import RuleCondition from './RuleCondition';

interface RuleGroupProps {
    node: EditorGroupNode;
    path: number[];
    depth: number;
    features: readonly ScreenerFeatureDefinition[];
    featureOptions: { value: string; label: string }[];
    onUpdateCondition: (path: number[], updater: (node: EditorConditionNode) => EditorConditionNode) => void;
    onSetGroupOperator: (path: number[], operator: ScreenerGroupOperator) => void;
    onAddCondition: (groupPath: number[]) => void;
    onAddGroup: (groupPath: number[]) => void;
    onRemoveNode: (path: number[]) => void;
}

const OPERATOR_STYLES: Record<ScreenerGroupOperator, { border: string; badge: string; label: string }> = {
    and: {
        border: 'border-l-blue-500',
        badge: 'bg-blue-900/60 text-blue-300 border-blue-700 hover:bg-blue-900/80',
        label: 'AND'
    },
    or: {
        border: 'border-l-amber-500',
        badge: 'bg-amber-900/60 text-amber-200 border-amber-700 hover:bg-amber-900/80',
        label: 'OR'
    },
    not: {
        border: 'border-l-red-500',
        badge: 'bg-red-900/60 text-red-300 border-red-700 hover:bg-red-900/80',
        label: 'NOT'
    }
};

const OPERATOR_OPTIONS: ScreenerGroupOperator[] = ['and', 'or', 'not'];

const RuleGroup = ({
    node,
    path,
    depth,
    features,
    featureOptions,
    onUpdateCondition,
    onSetGroupOperator,
    onAddCondition,
    onAddGroup,
    onRemoveNode
}: RuleGroupProps) => {
    const style = OPERATOR_STYLES[node.operator];

    const cycleOperator = () => {
        const currentIdx = OPERATOR_OPTIONS.indexOf(node.operator);
        const nextIdx = (currentIdx + 1) % OPERATOR_OPTIONS.length;
        onSetGroupOperator(path, OPERATOR_OPTIONS[nextIdx]);
    };

    const renderChild = (child: EditorNode, idx: number) => {
        const childPath = [...path, idx];

        if (child.type === 'group') {
            return (
                <RuleGroup
                    depth={depth + 1}
                    featureOptions={featureOptions}
                    features={features}
                    key={child.id}
                    node={child}
                    path={childPath}
                    onAddCondition={onAddCondition}
                    onAddGroup={onAddGroup}
                    onRemoveNode={onRemoveNode}
                    onSetGroupOperator={onSetGroupOperator}
                    onUpdateCondition={onUpdateCondition}
                />
            );
        }

        return (
            <RuleCondition
                featureOptions={featureOptions}
                features={features}
                key={child.id}
                node={child}
                path={childPath}
                onRemove={onRemoveNode}
                onUpdate={onUpdateCondition}
            />
        );
    };

    return (
        <div className="relative pl-5 ml-1">
            {/* Colored left border */}
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 border-l-2 ${style.border}`} />

            {/* Group header */}
            <div className="flex items-center gap-2 mb-2">
                <button
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold border cursor-pointer transition-colors ${style.badge}`}
                    title="Click to cycle: AND / OR / NOT"
                    type="button"
                    onClick={cycleOperator}
                >
                    {style.label}
                </button>

                <button
                    className="px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200 bg-zinc-800/60 hover:bg-zinc-800 rounded transition-colors"
                    type="button"
                    onClick={() => onAddCondition(path)}
                >
                    + Condition
                </button>

                <button
                    className="px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200 bg-zinc-800/60 hover:bg-zinc-800 rounded transition-colors"
                    type="button"
                    onClick={() => onAddGroup(path)}
                >
                    + Group
                </button>

                {depth > 0 && (
                    <button
                        className="px-2 py-1 text-xs text-red-400/70 hover:text-red-400 bg-zinc-800/60 hover:bg-zinc-800 rounded transition-colors"
                        type="button"
                        onClick={() => onRemoveNode(path)}
                    >
                        Remove Group
                    </button>
                )}
            </div>

            {/* Children */}
            <div className="space-y-2">
                {node.children.length === 0 && (
                    <div className="text-xs text-zinc-500 italic pl-1">Empty group — add a condition or nested group.</div>
                )}
                {node.children.map((child, idx) => renderChild(child, idx))}
            </div>
        </div>
    );
};

export default RuleGroup;
